// src/pages/marketplace/tenant/TenantProductSetupPage.tsx
// Multi-step wizard for subscribing a tenant to a new product.

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlanOption {
  id: string;
  name: string;
  price: string;
  period: string;
  features: string[];
  highlighted?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STEPS = ['dependencies', 'plan', 'confirmation'] as const;

/** Placeholder plan data until real plans API is available. */
const MOCK_PLANS: PlanOption[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/mo',
    features: ['Up to 5 users', '1 GB storage', 'Community support'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$29',
    period: '/mo',
    features: ['Up to 50 users', '50 GB storage', 'Priority support', 'Advanced analytics'],
    highlighted: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$99',
    period: '/mo',
    features: [
      'Unlimited users',
      '500 GB storage',
      'Dedicated support',
      'SSO & SAML',
      'Custom integrations',
    ],
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Step indicator dots with connecting line. */
function StepIndicator({
  steps,
  currentIndex,
  t,
}: {
  steps: readonly string[];
  currentIndex: number;
  t: (k: string) => string;
}) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((step, i) => {
        const isActive = i === currentIndex;
        const isCompleted = i < currentIndex;
        return (
          <div key={step} className="flex items-center">
            {/* Connector line */}
            {i > 0 && (
              <div
                className={`w-12 sm:w-20 h-0.5 ${
                  isCompleted ? 'bg-primary' : 'bg-border'
                }`}
              />
            )}
            {/* Dot + label */}
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  isCompleted
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                      ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-xs mt-1.5 whitespace-nowrap ${
                  isActive ? 'text-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                {t(`marketplace.setup.steps.${step}`)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * TenantProductSetupPage - wizard for subscribing to a new product.
 * Route: /settings/products/:id/setup
 */
export const TenantProductSetupPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useProduct();
  const { t } = useI18n();

  const [stepIndex, setStepIndex] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<string>('pro');
  const [validation, setValidation] = useState<any>(null);
  const [validating, setValidating] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const currentStep = STEPS[stepIndex];
  const product = store.currentItem;

  // Fetch product details on mount
  useEffect(() => {
    if (id) {
      store.GetProduct(id);
    }
    return () => {
      store.clearCurrent?.();
    };
  }, [id]);

  // Run dependency validation when we enter step 1
  useEffect(() => {
    if (currentStep === 'dependencies' && id && !validation && !validating) {
      setValidating(true);
      store
        .ValidateProductDependencies(id, {})
        .then((result: any) => {
          setValidation(result?.validation ?? result);
        })
        .catch(() => {
          // Error handled by store
        })
        .finally(() => {
          setValidating(false);
        });
    }
  }, [currentStep, id]);

  const canProceed = useCallback((): boolean => {
    if (currentStep === 'dependencies') {
      return validation?.is_valid === true;
    }
    if (currentStep === 'plan') {
      return !!selectedPlan;
    }
    return true;
  }, [currentStep, validation, selectedPlan]);

  const handleNext = () => {
    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleSubscribe = async () => {
    if (!id) return;
    setSubscribing(true);
    try {
      // TODO: Call actual subscription API when available
      // await store.SubscribeTenantToProduct(id, selectedPlan);
      navigate(`/settings/products/${id}`);
    } catch {
      // Error handled by store
    } finally {
      setSubscribing(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (store.isLoading && !product) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/marketplace" className="mt-2 inline-block text-sm underline text-primary">
          {t('marketplace.browse')}
        </Link>
      </>
    );
  }

  const productName = product?.name ?? '';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/settings/products" className="text-muted-foreground hover:text-foreground">
          {t('marketplace.tenant.title')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('marketplace.setup.title')}</span>
      </nav>

      {/* Page title */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {t('marketplace.setup.title')}
        </h1>
        {productName && (
          <p className="text-sm text-muted-foreground mt-1">{productName}</p>
        )}
      </div>

      {/* Step indicator */}
      <StepIndicator steps={STEPS} currentIndex={stepIndex} t={t} />

      {/* ================================================================= */}
      {/* Step 1: Dependency Check */}
      {/* ================================================================= */}
      {currentStep === 'dependencies' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('marketplace.setup.dependencies.title')}</CardTitle>
            <CardDescription>
              {t('marketplace.setup.dependencies.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {validating && (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                {t('common.loading')}
              </div>
            )}

            {!validating && validation && (
              <>
                {/* Hard failures */}
                {validation.hard_failures?.items?.length > 0 && (
                  <div className="space-y-2">
                    {validation.hard_failures.items.map((issue: any, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800"
                      >
                        <svg
                          className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-red-800 dark:text-red-300">
                            {issue.message}
                          </p>
                          {issue.resolution && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                              {issue.resolution}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Soft warnings */}
                {validation.soft_warnings?.items?.length > 0 && (
                  <div className="space-y-2">
                    {validation.soft_warnings.items.map((issue: any, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800"
                      >
                        <svg
                          className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                          />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                            {issue.message}
                          </p>
                          {issue.degraded_mode_message && (
                            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
                              {issue.degraded_mode_message}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recommendations */}
                {validation.recommendations?.items?.length > 0 && (
                  <div className="space-y-2">
                    {validation.recommendations.items.map((issue: any, idx: number) => {
                      const recProductId =
                        issue.required_product?.id ?? issue.required_product?.entity?.id;
                      return (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800"
                        >
                          <svg
                            className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                            />
                          </svg>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                              {issue.message}
                            </p>
                          </div>
                          {recProductId && (
                            <Link
                              to={`/settings/products/${recProductId}/setup`}
                              className="flex-shrink-0"
                            >
                              <Button variant="ghost" size="sm">
                                {t('marketplace.setup.dependencies.subscribe')}
                              </Button>
                            </Link>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* All clear */}
                {validation.is_valid &&
                  !validation.has_warnings &&
                  !validation.recommendations?.items?.length && (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
                      <svg
                        className="w-5 h-5 text-green-500 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <p className="text-sm font-medium text-green-800 dark:text-green-300">
                        {t('marketplace.setup.dependencies.allMet')}
                      </p>
                    </div>
                  )}

                {/* Valid with warnings */}
                {validation.is_valid && validation.has_warnings && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
                    <svg
                      className="w-5 h-5 text-green-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm font-medium text-green-800 dark:text-green-300">
                      {t('marketplace.setup.dependencies.validWithWarnings')}
                    </p>
                  </div>
                )}

                {/* Blocked */}
                {!validation.is_valid && (
                  <ErrorAlert error={t('marketplace.setup.dependencies.blocked')} />
                )}
              </>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Link to="/marketplace">
              <Button variant="ghost">{t('common.cancel')}</Button>
            </Link>
            <Button onClick={handleNext} disabled={!canProceed()}>
              {t('common.continue')}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* ================================================================= */}
      {/* Step 2: Plan Selection */}
      {/* ================================================================= */}
      {currentStep === 'plan' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('marketplace.setup.plan.title')}</CardTitle>
            <CardDescription>
              {t('marketplace.setup.plan.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {MOCK_PLANS.map((plan) => {
                const isSelected = selectedPlan === plan.id;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative flex flex-col rounded-xl border-2 p-5 text-left transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    {plan.highlighted && (
                      <Badge
                        variant="primary"
                        size="sm"
                        className="absolute -top-2.5 left-4"
                      >
                        {t('marketplace.setup.plan.popular')}
                      </Badge>
                    )}
                    <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                    <div className="mt-2 flex items-baseline gap-0.5">
                      <span className="text-2xl font-bold text-foreground">{plan.price}</span>
                      <span className="text-sm text-muted-foreground">{plan.period}</span>
                    </div>
                    <ul className="mt-4 space-y-2 flex-1">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <svg
                            className="w-4 h-4 text-green-500 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {/* Selected indicator */}
                    {isSelected && (
                      <div className="mt-4 flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <svg
                            className="w-3 h-3 text-primary-foreground"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="ghost" onClick={handleBack}>
              {t('common.back')}
            </Button>
            <Button onClick={handleNext} disabled={!canProceed()}>
              {t('common.continue')}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* ================================================================= */}
      {/* Step 3: Confirmation */}
      {/* ================================================================= */}
      {currentStep === 'confirmation' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('marketplace.setup.confirm.title')}</CardTitle>
            <CardDescription>
              {t('marketplace.setup.confirm.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <dl className="divide-y divide-border">
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('marketplace.setup.confirm.product')}
                </dt>
                <dd className="text-sm text-foreground col-span-2">{productName}</dd>
              </div>
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('marketplace.setup.confirm.plan')}
                </dt>
                <dd className="text-sm text-foreground col-span-2">
                  {MOCK_PLANS.find((p) => p.id === selectedPlan)?.name ?? selectedPlan}
                </dd>
              </div>
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('marketplace.setup.confirm.cost')}
                </dt>
                <dd className="text-sm text-foreground col-span-2">
                  {MOCK_PLANS.find((p) => p.id === selectedPlan)?.price ?? '--'}
                  {MOCK_PLANS.find((p) => p.id === selectedPlan)?.period ?? ''}
                </dd>
              </div>
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('marketplace.setup.confirm.dependencies')}
                </dt>
                <dd className="text-sm col-span-2">
                  {validation?.is_valid ? (
                    <Badge variant="success" size="sm">
                      {t('marketplace.setup.dependencies.allMet')}
                    </Badge>
                  ) : (
                    <Badge variant="error" size="sm">
                      {t('marketplace.setup.dependencies.blocked')}
                    </Badge>
                  )}
                </dd>
              </div>
            </dl>
          </CardContent>
          <CardFooter className="flex justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleBack}>
                {t('common.back')}
              </Button>
              <Link to="/marketplace">
                <Button variant="ghost">{t('common.cancel')}</Button>
              </Link>
            </div>
            <Button
              onClick={handleSubscribe}
              disabled={subscribing}
              className="min-w-[140px]"
            >
              {subscribing ? t('common.loading') : t('marketplace.setup.confirm.subscribe')}
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
});

export default TenantProductSetupPage;
